/**
 * @lines98/client/screens/remote-replay — remote-replay mount + fallback.
 *
 * One cohesive concept: mount a REMOTE replay by `short_id` (deep-link
 * `/r/<short_id>` or in-shell leaderboard nav) — fetch `GET /api/replay/<short_id>`,
 * branch to player (PLAYABLE) / teaser (TEASER) / «недоступен» fallback STRICTLY
 * per the server `kind` (replays-ghosts.md §5.4 / §5.5 — the client never decides
 * the gate), and render the «Просмотр игры недоступен» fallback (§7.2 / AC-10) on
 * not_found / network / server_error. The player-visible label «Реплей» is renamed
 * to «Просмотр игры» (fe-overlays П3 / AC-П3-1); all visible strings resolve from
 * `lib/i18n` (ru/en) — no RU literals in markup. The code term `replay`, the engine
 * terminology (`seed+moves`) and the deep-link URL `/r/<id>` are NOT renamed.
 *
 * Extracted from `app.ts` (kept the phase router thin per the modular directive
 * 2026-05-30 — code-structure-conventions §3 / §10.1). Behaviour is unchanged.
 *
 * Дискета «Сохранить в Книгу» (AC-B7, fix-цикл 2026-06-24): И playable-плеер, И
 * ТИЗЕР чужой партии монтируют ОБЩИЙ kit `lib/replay-save-button` (через `foreignSave`).
 * Для тизера ownership не гадаем: TEASER возвращается сервером ТОЛЬКО для чужой
 * сегодняшней daily (replays-ghosts.md §2.6 — TEASER ⟺ viewer ≠ owner; свой = PLAYABLE).
 */

import { openLoginFlow } from '../lib/auth-flow.js';
import { resolveBootLocaleSync, setLocale, t } from '../lib/i18n/index.js';
import { isAuthed } from '../services/identity.js';
import { getReplay, type ReplayResult } from '../services/replay-api.js';
import { useGameStore } from '../store/game-store.js';
import { type ScreenHandle } from './board-screen.js';
import { mountReplayScreen, type ForeignSaveOptions, type OwnShareOptions } from './replay-screen.js';
import { mountReplayTeaserScreen } from './replay-teaser-screen.js';

/**
 * Mount a REMOTE replay by `short_id`: fetch `GET /api/replay/<short_id>` then
 * branch to the player (PLAYABLE) / teaser (TEASER) / not-found view STRICTLY per
 * the server `kind` (replays-ghosts.md §5.4/§5.5 — the client never decides the
 * gate). Returns a synchronous `ScreenHandle`; the fetch resolves asynchronously
 * into the host, and `unmount()` both cancels the stale resolution and tears down
 * whichever inner screen mounted.
 *
 * CTA targets are in-shell (store transitions, no page reload): «Сыграть
 * сегодняшнюю доску» / exit / not-found CTA all route to the daily hub.
 */
export function mountRemoteReplay(host: HTMLElement, shortId: string): ScreenHandle {
  let cancelled = false;
  let inner: ScreenHandle | null = null;
  const goDaily = (): void => {
    useGameStore.getState().showDaily();
  };

  // Render a neutral container synchronously (handle is sync); visible i18n text
  // is shown only AFTER the boot-resolved locale catalog is guaranteed loaded. On a
  // cold deep-link (`/r/<id>` opened directly) the main.ts boot-locale load is
  // fire-and-forget and races this render — without the guard `t()` would flash a
  // raw key OR the wrong language (the runtime default `en` before boot resolves the
  // user's `ru`). `resolveBootLocaleSync(null)` reproduces boot's precedence (explicit
  // localStorage > browser > en) for this prefixless deep-link path, and `setLocale`
  // both loads the catalog AND activates it (idempotent / cached). Same readiness
  // discipline as the helper-unlock overlay before painting its i18n label
  // (app.ts demo='helperunlock').
  host.innerHTML = '';

  void (async () => {
    await setLocale(resolveBootLocaleSync(null));
    if (cancelled) return;

    // Loading placeholder until the fetch resolves.
    host.innerHTML = '';
    const loading = document.createElement('div');
    // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
    loading.className = 'replay-fallback replay-loading screen-root';
    loading.textContent = t('game.replayUnavailable.loading');
    host.appendChild(loading);

    let result: ReplayResult;
    try {
      result = await getReplay(shortId);
    } catch {
      if (cancelled) return;
      inner = mountReplayUnavailable(host, 'network', goDaily);
      return;
    }
    if (cancelled) return;
    if (result.ok && result.kind === 'playable') {
      // PLAYABLE — local player fed the server source (same SHARED engine).
      // Кнопка плеера — РОВНО одна ветка по server-truth `isOwn` (реплей-под-волна AC-8):
      //   · СВОЙ реплей (`isOwn === true`) → кнопка «Поделиться» (ICON_SHARE) → copyShareLink;
      //   · ЧУЖОЙ (`isOwn !== true`, вкл. `undefined` от старого сервера) → дискета «Сохранить».
      // Решение «чужой/свой» — СЕРВЕРНОЕ (`isOwn` из replay-api), НЕ клиентский guess.
      // Гость на ЧУЖОМ → клик дискеты даёт попап-промпт (AC-3 гость), не редирект на логин.
      const isOwn = result.replay.isOwn === true;
      const ownShare: OwnShareOptions | undefined = isOwn
        ? { shortId: result.replay.shortId }
        : undefined;
      // `savedInitially` — server-truth «уже в архиве «Чужие»» (owner-bug 2026-06-25):
      // уже сохранённый чужой реплей открывается с СЕРОЙ/disabled дискетой СРАЗУ, без
      // клика. Берём из `result.replay.bookmarked` (replay-api); отсутствие поля (старый
      // сервер → undefined) → дискета стартует активной (прежнее поведение, fallback).
      const foreignSave: ForeignSaveOptions | undefined = isOwn
        ? undefined
        : {
            shortId: result.replay.shortId,
            isGuest: !isAuthed(),
            onRequestLogin: () => openLoginFlow(),
            ...(result.replay.bookmarked === true ? { savedInitially: true } : {}),
          };
      inner = mountReplayScreen(host, {
        source: {
          seed: result.replay.seed,
          moves: result.replay.moves,
          mode: result.replay.mode,
        },
        onExit: goDaily,
        ...(ownShare ? { ownShare } : {}),
        ...(foreignSave ? { foreignSave } : {}),
      });
    } else if (result.ok && result.kind === 'teaser') {
      // TEASER — text-only surface; NO board/moves (foundational invariant).
      // Дискета «Сохранить в Книгу» (AC-B7): TEASER возвращается СЕРВЕРОМ только для
      // ЧУЖОЙ сегодняшней daily (replays-ghosts.md §2.6 таблица — TEASER ⟺ viewer ≠ owner;
      // свой реплей всегда PLAYABLE по own-carve-out §3A.2). Значит teaser ⟹ партия чужая ⟹
      // её можно закладывать. Натур-ключ закладки — `shortId` deep-link'а (тот же id, что
      // отдал страницу). Гость → клик дискеты ведёт на логин (AC-B6, единый auth-flow).
      const foreignSave: ForeignSaveOptions = {
        shortId,
        isGuest: !isAuthed(),
        onRequestLogin: () => openLoginFlow(),
      };
      inner = mountReplayTeaserScreen(
        host,
        {
          scoreVerified: result.teaser.scoreVerified,
          rank: result.teaser.rank,
          movesCount: result.teaser.movesCount,
          countdownToRolloverSeconds: result.teaser.countdownToRolloverSeconds,
        },
        goDaily,
        foreignSave,
      );
    } else {
      // not_found / network / server_error → «Просмотр игры недоступен» + CTA (AC-10).
      inner = mountReplayUnavailable(host, result.ok ? 'not_found' : result.reason, goDaily);
    }
  })();

  return {
    unmount(): void {
      cancelled = true;
      if (inner) inner.unmount();
      host.innerHTML = '';
    },
  };
}

/**
 * Render the «Просмотр игры недоступен» fallback (replays-ghosts.md §7.2 / AC-10): a
 * clear message + a CTA to play the daily board, never a 500 / blank. All visible
 * strings come from `lib/i18n` (ru/en) — `game.replayUnavailable.*` (fe-overlays
 * П2 i18n + П3 «Реплей»→«Просмотр игры» rename).
 */
function mountReplayUnavailable(
  host: HTMLElement,
  reason: 'not_found' | 'network' | 'server_error',
  onCta: () => void,
): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'replay-fallback replay-unavailable screen-root';
  const lead =
    reason === 'not_found'
      ? t('game.replayUnavailable.leadNotFound')
      : t('game.replayUnavailable.leadError');
  root.innerHTML = `
    <h2 class="screen-title">${t('game.replayUnavailable.title')}</h2>
    <p class="replay-unavailable-lead">${lead}</p>
    <div class="replay-unavailable-actions">
      <button class="btn primary" id="rp-unavail-cta" type="button">${t('game.replayUnavailable.cta')}</button>
    </div>
  `;
  host.appendChild(root);
  root.querySelector<HTMLButtonElement>('#rp-unavail-cta')?.addEventListener('click', onCta);
  return {
    unmount(): void {
      host.innerHTML = '';
    },
  };
}
